import { DateTime, type Zone } from 'luxon';

export const CONVERSION_RECOMMENDATION_ELEMENTS = [
  'LIVE_OFFER',
  'PURCHASE_NOTIFICATIONS',
  'CONVERSION_LABEL',
  'THANK_YOU_PAGE_OFFER',
] as const;

export type ConversionRecommendationElement = (typeof CONVERSION_RECOMMENDATION_ELEMENTS)[number];

export type ConversionRecommendation = {
  id: number;
  sessionId: number;
  webinarId: number;
  plannedDate: string;
  missingElements: ConversionRecommendationElement[];
};

export type ConversionRecommendationTiming = 'today' | 'tomorrow' | 'upcoming';

// L'API date les sessions en UTC sans suffixe de fuseau : sans le `zone: 'utc'`,
// Luxon comme Date les liraient en heure locale.
function toLocalDateTime(utcPlannedDate: string, zone: Zone): DateTime {
  return DateTime.fromISO(utcPlannedDate, { zone: 'utc' }).setZone(zone);
}

// Le back contrôle les sessions à venir dans les 24 heures, pas uniquement à H-24 :
// le titre suit donc la date réelle du live plutôt que d'annoncer « demain ».
export function resolveRecommendationTiming(
  utcPlannedDate: string,
  now: DateTime = DateTime.local(),
): ConversionRecommendationTiming {
  const plannedDate = toLocalDateTime(utcPlannedDate, now.zone);
  if (!plannedDate.isValid) {
    return 'upcoming';
  }

  const days = plannedDate.startOf('day').diff(now.startOf('day'), 'days').days;
  if (days <= 0) {
    return 'today';
  }
  return days === 1 ? 'tomorrow' : 'upcoming';
}

const KEY_PREFIX = 'welya.conversion-recommendation-dismissed.';

function storageKey(recommendationId: number): string {
  return `${KEY_PREFIX}${recommendationId}`;
}

export function isConversionRecommendationDismissed(recommendationId: number): boolean {
  try {
    return window.sessionStorage.getItem(storageKey(recommendationId)) !== null;
  } catch {
    // stockage indisponible (navigation privée, bloqueur) : la modale se rouvrira, sans casse
    return false;
  }
}

export function markConversionRecommendationDismissed(recommendationId: number): void {
  try {
    window.sessionStorage.setItem(storageKey(recommendationId), '1');
  } catch {
    // idem : la fermeture reste effective pour la visite en cours
  }
}

function isKnownElement(value: string): value is ConversionRecommendationElement {
  return (CONVERSION_RECOMMENDATION_ELEMENTS as readonly string[]).includes(value);
}

// L'API renvoie un Set : on fige l'ordre sur celui du parcours de configuration.
export function normalizeMissingElements(values: string[]): ConversionRecommendationElement[] {
  return CONVERSION_RECOMMENDATION_ELEMENTS.filter((element) =>
    values.filter(isKnownElement).includes(element),
  );
}

function plannedTimestamp(recommendation: ConversionRecommendation): number {
  return DateTime.fromISO(recommendation.plannedDate, { zone: 'utc' }).toMillis();
}

export function selectRecommendationToDisplay(
  recommendations: ConversionRecommendation[],
  isDismissed: (recommendationId: number) => boolean,
): ConversionRecommendation | null {
  const displayable = recommendations
    .filter((recommendation) => recommendation.missingElements.length > 0)
    .filter((recommendation) => !isDismissed(recommendation.id));

  if (displayable.length === 0) {
    return null;
  }

  return displayable.reduce((closest, candidate) =>
    plannedTimestamp(candidate) < plannedTimestamp(closest) ? candidate : closest,
  );
}
